import React, { createContext, useContext, useState } from "react";

export interface CartItem {
  cartId: string;
  service_number: string;
  carrier: string;
  carrier_name: string;
  board_time?: string;
  destination_time?: string;
  fare: number;
  available_seats: number;
  board_date?: string;
  destination_date?: string;
  board_description?: string;
  destination_description?: string;
  boardlocat?: string;
  destlocat?: string;
  duration?: string;
}

export interface Passenger {
  passengerIndex: number;
  passengerType: "Adult" | "Child";
  title: string;
  surname: string;
  initials: string;
  idPassportCountry: string;
  cellphone: string;
  email: string;
  emergencyContactName: string;
  emergencyContactTelephone: string;
}

export interface SearchPassengers {
  adultsCount: number;
  childrenCount: number;
  tripType?: "one-way" | "return";
  returnDate?: string;
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (cartId: string) => void;
  clearCart: () => void;
  cartTotal: number;
  isCheckout: boolean;
  setIsCheckout: (value: boolean) => void;
  passengers: Passenger[];
  setPassengers: (passengers: Passenger[]) => void;
  searchPassengers: SearchPassengers;
  setSearchPassengers: (passengers: SearchPassengers) => void;
  hasSearched: boolean;
  setHasSearched: (value: boolean) => void;
  sessionId: string | null;
  setSessionId: (sessionId: string | null) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const defaultSearchPassengers: SearchPassengers = {
  adultsCount: 1,
  childrenCount: 0,
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCheckout, setIsCheckout] = useState(false);
  const [passengers, setPassengers] = useState<Passenger[]>([]);
  const [searchPassengers, setSearchPassengers] = useState<SearchPassengers>(defaultSearchPassengers);
  const [hasSearched, setHasSearched] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const addToCart = (item: CartItem) => {
    setCart([...cart, item]);
  };

  const removeFromCart = (cartId: string) => {
    setCart(cart.filter(item => item.cartId !== cartId));
  };

  const clearCart = () => {
    setCart([]);
    setIsCheckout(false);
    setPassengers([]);
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.fare || 0), 0);

  return (
    <CartContext.Provider 
      value={{ 
        cart, 
        addToCart, 
        removeFromCart, 
        clearCart, 
        cartTotal,
        isCheckout,
        setIsCheckout,
        passengers,
        setPassengers,
        searchPassengers,
        setSearchPassengers,
        hasSearched,
        setHasSearched,
        sessionId,
        setSessionId
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};
